import { ForwardedRef, forwardRef, PropsWithChildren } from 'react'

import { useTheme } from '@emotion/react'

import { ThemeVariantProvider } from '@cash-design-system/react'
import { FlowRenderer } from '@cash-web/shared-blocker-feature-main'
import { combineRefs } from '@cash-web/shared-feature-form'
import { Modal, ModalPane } from '@cash-web/shared-feature-modal'
import { FlowProvider } from '@cash-web/shared-flow-feature-main'
import { JourneyContextProps, JourneyParentContext } from '@cash-web/shared-journey-data-access-context'
import { JourneyData, JourneyScope, JourneyTarget } from '@cash-web/shared-journey-util-main'

import { getModalVariant, useTargetHistory } from './getTargetHistory'
import { useJourneyFlowInputFocus } from './hooks/useJourneyFlowInputFocus'
import { useJourneyScrollPosition } from './hooks/useJourneyScrollPosition'
import { JourneyBaggageMiddleware } from './JourneyBaggageMiddleware'
import { JourneyPaneRouter } from './JourneyPaneRouter'
import { useJourneyStore } from './JourneyStoreProvider'

export type JourneyTargetRendererProps = {
  history: JourneyData[]
  onBack?: JourneyContextProps['cancel']
  onClose: JourneyContextProps['cancelAll']
  target: JourneyTarget
}
export const JourneyTargetRenderer = ({ history, onBack, onClose, target, ...props }: JourneyTargetRendererProps) => {
  const theme = useTheme()
  const targetHistory = useTargetHistory(history)[target] ?? []
  const active = targetHistory.at(-1)

  const storeInstance = useJourneyStore(active?.journeyId ?? '')
  const flow = storeInstance?.flow ?? undefined
  const router = storeInstance?.router ?? undefined

  const { ref: focusRef } = useJourneyFlowInputFocus({ isActive: true, flow })
  const { ref: scrollRef } = useJourneyScrollPosition(targetHistory.at(-1)?.journeyId)

  const ref = combineRefs(focusRef, scrollRef) as React.Ref<HTMLDivElement>

  if (targetHistory.length === 0 || active == null || router == null) {
    return null
  }

  /**
   * Captures the focused element when a flow is changing blocker views and when a journey is active and the first element is focused
   */

  const handleCancelActionWithFlow = (cancelAction?: () => void) => {
    if (flow?.isInProgress) {
      // this overrides the default cancel flow behavior, and waits for any dismiss action to be triggered and then calls the cancelAction
      // if the user actually cancelled the flow, we call the cancelAction
      flow.cancel({
        onCancelFlowOverride: () => {
          cancelAction?.()
        },
      })
    } else {
      cancelAction?.()
    }
  }

  const handleClose = () => {
    handleCancelActionWithFlow?.(() => onClose({ rootId: active?.rootId }))
  }

  const shouldHideBack = target !== 'fullscreen' && flow?.isLoading
  const getBackHandler = () => {
    if ((flow?.isInProgress && flow.active?.blockerData?.id === 'FLOW_TERMINATION') || shouldHideBack) {
      return undefined
    }
    if (targetHistory.length === 1 && target === 'modal') {
      return undefined
    }
    return () => handleCancelActionWithFlow?.(() => onBack?.(active?.journeyId))
  }

  const handleBack = getBackHandler()
  const scope = active?.opts?.scope as JourneyScope | undefined
  return (
    <JourneyParentContext.Provider value={active}>
      <JourneyBaggageMiddleware journey={active} flow={flow}>
        <ThemeVariantProvider themeVariant={scope?.themeVariant ?? theme.themeVariant}>
          <JourneyTargetContainer
            ref={ref}
            target={target}
            journeyData={active}
            onClose={handleClose}
            onBack={handleBack}
          >
            {/* we want dont want to unmount the container when the flow is being created */}
            {flow == null ? null : (
              <FlowProvider flow={flow}>
                {flow?.isInProgress ? <FlowRenderer /> : <JourneyPaneRouter key={active.journeyId} router={router} />}
              </FlowProvider>
            )}
          </JourneyTargetContainer>
        </ThemeVariantProvider>
      </JourneyBaggageMiddleware>
    </JourneyParentContext.Provider>
  )
}

export const JourneyTargetContainer = forwardRef(
  (
    {
      target,
      journeyData,
      onClose,
      onBack,
      children,
    }: PropsWithChildren<{
      target: JourneyTarget
      journeyData: JourneyData
      onClose: () => void
      onBack: (() => void) | undefined
    }>,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const flow = useJourneyStore(journeyData.journeyId)?.flow
    const scope = journeyData?.opts?.scope as JourneyScope | undefined
    const modalVariant = getModalVariant(journeyData) ?? 'default'
    switch (target) {
      case 'modal':
        return (
          <Modal
            ref={ref}
            // we should not show the receipt modal when the flow is in progress
            variant={flow?.isInProgress && modalVariant === 'receipt' ? 'default' : modalVariant}
            onClose={onClose}
            onBack={onBack}
            disableAnimation={scope?.disableModalAnimation}
            overflowScroll={scope?.overflowScroll}
            dataTestId={journeyData.path}
          >
            {children}
          </Modal>
        )

      case 'fullscreen':
        return (
          <ModalPane ref={ref} onBack={onBack} onClose={onClose} dataTestId={journeyData.path}>
            {children}
          </ModalPane>
        )

      default:
        return <div ref={ref}>{children}</div>
    }
  }
)
